// components/Support/SupportLiveAlert.tsx
//
// Single global Pusher hub. Mounted once in App.tsx outside <Routes>.
// Admins  → private-support.admin (new messages) + private-support.ticket.{id} (typing)
// Users   → private-support.ticket.{id} per ticket (new messages + typing + status)
// Both    → toast notifications for incoming messages

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  addIncomingMessage,
  setTypingIndicator,
  setAdminTypingIndicator,
  updateTicketStatus,
  getUserTickets,
  adminGetAllTickets,
  adminGetTicketMessages,
} from "../../store/slices/supportSlice";
import {
  getPusherInstance,
  SupportMessage,
} from "../../services/supportService";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

interface AlertMessage extends SupportMessage {
  ticket_subject?: string;
  ticket_user_name?: string;
  ticket_user_email?: string;
}

interface LiveAlert {
  id: string;
  message: AlertMessage;
  visible: boolean;
}

// ─── Toast card ───────────────────────────────────────────────────────────────

const AlertToast: React.FC<{
  alert: LiveAlert;
  isAdmin: boolean;
  onDismiss: (id: string) => void;
  onClick: (msg: AlertMessage) => void;
}> = ({ alert, isAdmin, onDismiss, onClick }) => {
  const { message } = alert;
  const senderLabel = isAdmin
    ? `${message.ticket_user_name ?? "User"}${message.ticket_user_email ? ` · ${message.ticket_user_email}` : ""}`
    : "Support Team";
  const avatarLetter = isAdmin
    ? (message.ticket_user_name?.[0]?.toUpperCase() ?? "U")
    : (message.ticket_subject?.[0]?.toUpperCase() ?? "S");
  const previewText = message.message
    ? message.message.length > 80
      ? message.message.slice(0, 80) + "…"
      : message.message
    : message.attachment_name
      ? `📎 ${message.attachment_name}`
      : "New message";

  return (
    <div
      className={`flex items-center gap-2.5 bg-gray-900 border border-gray-700 rounded-xl
        shadow-xl px-3 py-2.5 w-[280px] max-w-[calc(100vw-2rem)] cursor-pointer
        transition-all duration-300 ease-out hover:border-orange-500/60 hover:bg-gray-800
        ${alert.visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full"}`}
      style={{ willChange: "transform, opacity" }}
      onClick={() => onClick(message)}
    >
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-bold text-black">{avatarLetter}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-orange-400 truncate">
          {isAdmin ? "🎫 New message" : "💬 Support reply"}
        </p>
        {!isAdmin && message.ticket_subject && (
          <p className="text-xs text-gray-500 truncate leading-none mb-0.5">
            Re: {message.ticket_subject}
          </p>
        )}
        <p className="text-xs text-gray-300 truncate leading-snug">
          {previewText}
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(alert.id);
        }}
        className="p-0.5 text-gray-500 hover:text-gray-300 flex-shrink-0 transition-colors"
      >
        <XMarkIcon className="w-3 h-3" />
      </button>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const SupportLiveAlert: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);
  const { tickets, adminActiveTicket, activeTicketId } = useSelector(
    (s: RootState) => s.support,
  );

  const [alerts, setAlerts] = useState<LiveAlert[]>([]);
  const isAdmin = user?.role === "admin";

  // Track channel subscriptions to avoid duplicates
  const subscribedRef = useRef<Set<string>>(new Set());
  const timerRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const typingTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // ── Toast helpers ──────────────────────────────────────────────────────────

  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, visible: false } : a)),
    );
    setTimeout(() => setAlerts((prev) => prev.filter((a) => a.id !== id)), 300);
    const t = timerRefs.current.get(id);
    if (t) {
      clearTimeout(t);
      timerRefs.current.delete(id);
    }
  }, []);

  const addAlert = useCallback(
    (msg: AlertMessage) => {
      const id = `alert-${Date.now()}-${msg.id}`;
      setAlerts((prev) => {
        const trimmed = prev.length >= 3 ? prev.slice(1) : prev;
        return [...trimmed, { id, message: msg, visible: false }];
      });
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          setAlerts((prev) =>
            prev.map((a) => (a.id === id ? { ...a, visible: true } : a)),
          );
        }),
      );
      timerRefs.current.set(
        id,
        setTimeout(() => dismissAlert(id), 6000),
      );
    },
    [dismissAlert],
  );

  const handleAlertClick = useCallback(
    (msg: AlertMessage) => {
      if (isAdmin) {
        navigate("/admin/support");
        dispatch(adminGetAllTickets(undefined));
        dispatch(adminGetTicketMessages(msg.ticket_id));
      } else {
        import("../../store/slices/supportSlice").then((mod) => {
          dispatch(mod.setActiveTicketId(msg.ticket_id));
          dispatch(mod.markTicketRead(msg.ticket_id));
          dispatch(mod.getTicketMessages(msg.ticket_id));
        });
        navigate("/support");
      }
    },
    [isAdmin, navigate, dispatch],
  );

  // ── ADMIN: subscribe to private-support.admin (new messages from users) ────

  useEffect(() => {
    if (!user || !isAdmin) return;
    const pusher = getPusherInstance();
    const key = "admin-global";
    if (subscribedRef.current.has(key)) return;

    const ch = pusher.subscribe("private-support.admin");
    subscribedRef.current.add(key);

    ch.bind("new.message", (data: any) => {
      const msg: AlertMessage = data.message ?? data;
      if (msg.sender_type === "user") {
        dispatch(addIncomingMessage(msg as SupportMessage));
        addAlert(msg);
        dispatch(adminGetAllTickets(undefined));
      }
    });

    ch.bind("ticket.status.updated", (data: any) => {
      dispatch(
        updateTicketStatus({ ticketId: data.ticket_id, status: data.status }),
      );
    });

    return () => {
      ch.unbind_all();
      pusher.unsubscribe("private-support.admin");
      subscribedRef.current.delete(key);
    };
  }, [user, isAdmin, dispatch, addAlert]);

  // ── ADMIN: subscribe to active ticket channel for TYPING events ────────────
  // UserTyping only broadcasts on private-support.ticket.{id}, not on admin channel

  useEffect(() => {
    if (!user || !isAdmin || !adminActiveTicket) return;
    const pusher = getPusherInstance();
    const ticketId = adminActiveTicket.id;
    const key = `admin-ticket-${ticketId}`;

    // Unsubscribe from any previous admin-ticket-* channel
    subscribedRef.current.forEach((k) => {
      if (k.startsWith("admin-ticket-") && k !== key) {
        const oldId = k.replace("admin-ticket-", "");
        try {
          pusher.unsubscribe(`private-support.ticket.${oldId}`);
        } catch {
          /**/
        }
        subscribedRef.current.delete(k);
      }
    });

    if (subscribedRef.current.has(key)) return;

    const ch = pusher.subscribe(`private-support.ticket.${ticketId}`);
    subscribedRef.current.add(key);

    ch.bind("typing", (data: any) => {
      // Admin sees user typing
      if (data.sender_type === "user") {
        dispatch(
          setAdminTypingIndicator({
            isTyping: data.is_typing,
            senderName: data.sender_name,
            ticketId: data.ticket_id,
          }),
        );
        // Auto-clear after 4s in case stop event never arrives
        const tid: number = data.ticket_id;
        const existing = typingTimers.current.get(tid);
        if (existing) clearTimeout(existing);
        if (data.is_typing) {
          typingTimers.current.set(
            tid,
            setTimeout(() => {
              dispatch(
                setAdminTypingIndicator({
                  isTyping: false,
                  senderName: null,
                  ticketId: tid,
                }),
              );
            }, 4000),
          );
        }
      }
    });

    // Also handle new.message on the ticket channel for real-time admin messages view
    ch.bind("new.message", (data: any) => {
      const msg = data.message ?? data;
      dispatch(addIncomingMessage(msg as SupportMessage));
    });

    return () => {
      ch.unbind_all();
      pusher.unsubscribe(`private-support.ticket.${ticketId}`);
      subscribedRef.current.delete(key);
    };
  }, [user, isAdmin, adminActiveTicket?.id, dispatch]);

  // ── USER: subscribe to the ACTIVE ticket channel immediately ─────────────────
  // This fires as soon as a ticket is selected, even before getUserTickets resolves.
  // The tickets-loop below handles background notifications for non-active tickets.

  useEffect(() => {
    if (!user || isAdmin || !activeTicketId) return;
    const pusher = getPusherInstance();
    const key = `user-active-${activeTicketId}`;

    if (subscribedRef.current.has(key)) return;

    const ch = pusher.subscribe(`private-support.ticket.${activeTicketId}`);
    subscribedRef.current.add(key);

    ch.bind("new.message", (data: any) => {
      const msg: SupportMessage = data.message ?? data;
      dispatch(addIncomingMessage(msg));
      if (msg.sender_type === "admin") addAlert(msg as AlertMessage);
    });

    ch.bind("typing", (data: any) => {
      if (data.sender_type === "admin") {
        dispatch(
          setTypingIndicator({
            isTyping: data.is_typing,
            senderName: data.sender_name,
            ticketId: data.ticket_id,
          }),
        );
        const tid: number = data.ticket_id;
        const existing = typingTimers.current.get(tid);
        if (existing) clearTimeout(existing);
        if (data.is_typing) {
          typingTimers.current.set(
            tid,
            setTimeout(() => {
              dispatch(
                setTypingIndicator({
                  isTyping: false,
                  senderName: null,
                  ticketId: tid,
                }),
              );
            }, 4000),
          );
        }
      }
    });

    ch.bind("ticket.status.updated", (data: any) => {
      dispatch(
        updateTicketStatus({ ticketId: data.ticket_id, status: data.status }),
      );
    });

    return () => {
      // Don't unsubscribe immediately — the tickets-loop may re-use the same channel.
      // Just remove the key so the tickets-loop can re-bind if needed.
      subscribedRef.current.delete(key);
    };
  }, [user, isAdmin, activeTicketId, dispatch, addAlert]);

  // ── USER: subscribe to each owned ticket channel ───────────────────────────

  useEffect(() => {
    if (!user || isAdmin) return;
    const pusher = getPusherInstance();

    tickets.forEach((ticket) => {
      const key = `user-ticket-${ticket.id}`;
      const activeKey = `user-active-${ticket.id}`;
      // Already subscribed by the active-ticket effect above — skip to avoid double bind
      if (
        subscribedRef.current.has(key) ||
        subscribedRef.current.has(activeKey)
      )
        return;

      const ch = pusher.subscribe(`private-support.ticket.${ticket.id}`);
      subscribedRef.current.add(key);

      ch.bind("new.message", (data: any) => {
        const msg: SupportMessage = data.message ?? data;
        dispatch(addIncomingMessage(msg));
        if (msg.sender_type === "admin") {
          addAlert(msg as AlertMessage);
        }
      });

      ch.bind("typing", (data: any) => {
        // User sees admin typing
        if (data.sender_type === "admin") {
          dispatch(
            setTypingIndicator({
              isTyping: data.is_typing,
              senderName: data.sender_name,
              ticketId: data.ticket_id,
            }),
          );
          const tid: number = data.ticket_id;
          const existing = typingTimers.current.get(tid);
          if (existing) clearTimeout(existing);
          if (data.is_typing) {
            typingTimers.current.set(
              tid,
              setTimeout(() => {
                dispatch(
                  setTypingIndicator({
                    isTyping: false,
                    senderName: null,
                    ticketId: tid,
                  }),
                );
              }, 4000),
            );
          }
        }
      });

      ch.bind("ticket.status.updated", (data: any) => {
        dispatch(
          updateTicketStatus({ ticketId: data.ticket_id, status: data.status }),
        );
      });
    });
  }, [user, isAdmin, tickets, dispatch, addAlert]);

  // ── ADMIN: initial data load ───────────────────────────────────────────────

  useEffect(() => {
    if (!user || !isAdmin) return;
    dispatch(adminGetAllTickets(undefined));
  }, [user, isAdmin, dispatch]);

  // ── USER: initial data load ────────────────────────────────────────────────

  useEffect(() => {
    if (!user || isAdmin) return;
    // getUserTickets returns unread_count per ticket — the slice sums them into unreadCount.
    // No separate getUnreadCount call needed (it would race and overwrite the correct value).
    dispatch(getUserTickets());
  }, [user, isAdmin, dispatch]);

  // ── Full cleanup on unmount ────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      timerRefs.current.forEach((t) => clearTimeout(t));
      typingTimers.current.forEach((t) => clearTimeout(t));
      const pusher = getPusherInstance();
      subscribedRef.current.forEach((key) => {
        try {
          if (key === "admin-global")
            pusher.unsubscribe("private-support.admin");
          else if (key.startsWith("admin-ticket-")) {
            const id = key.replace("admin-ticket-", "");
            pusher.unsubscribe(`private-support.ticket.${id}`);
          } else if (key.startsWith("user-ticket-")) {
            const id = key.replace("user-ticket-", "");
            pusher.unsubscribe(`private-support.ticket.${id}`);
          }
        } catch {
          /**/
        }
      });
      subscribedRef.current.clear();
    };
  }, []);

  if (!user || alerts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[200] flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
    >
      {alerts.map((alert, i) => (
        <div
          key={alert.id}
          className="pointer-events-auto"
          style={{ zIndex: 200 + i }}
        >
          <AlertToast
            alert={alert}
            isAdmin={isAdmin}
            onDismiss={dismissAlert}
            onClick={handleAlertClick}
          />
        </div>
      ))}
    </div>
  );
};

export default SupportLiveAlert;
